import React, { useCallback, useEffect, useRef, useState } from 'react';

import { appMaintenanceCheck, isMaintenanceModeError } from '../actions/auth';
import { isMaintenanceBypassEnabled } from '../utils/maintenanceBypass';
import { useMaintenanceBypass } from '../utils/useMaintenanceBypass';
import MaintenanceModal from './MaintenanceModal';

const MAINTENANCE_POLL_INTERVAL_MS = 60_000;

type MaintenanceGateProps = {
  children: React.ReactNode;
};

const MaintenanceGate = ({ children }: MaintenanceGateProps) => {
  const bypassEnabled = useMaintenanceBypass();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollIntervalRef = useRef<number | null>(null);

  const clearMaintenancePoll = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const checkMaintenanceMode = useCallback(() => {
    if (isMaintenanceBypassEnabled()) {
      setRetrying(false);
      setMaintenanceMode(false);
      return;
    }

    appMaintenanceCheck(
      () => {
        setRetrying(false);
        setMaintenanceMode(false);
      },
      (error) => {
        setRetrying(false);
        setMaintenanceMode(isMaintenanceModeError(error));
      },
    );
  }, []);

  const handleRetry = useCallback(() => {
    setRetrying(true);
    checkMaintenanceMode();
  }, [checkMaintenanceMode]);

  useEffect(() => {
    if (bypassEnabled) {
      setMaintenanceMode(false);
      setRetrying(false);
      return undefined;
    }

    checkMaintenanceMode();
  }, [bypassEnabled, checkMaintenanceMode]);

  useEffect(() => {
    clearMaintenancePoll();

    if (bypassEnabled || !maintenanceMode) {
      return undefined;
    }

    pollIntervalRef.current = window.setInterval(() => {
      checkMaintenanceMode();
    }, MAINTENANCE_POLL_INTERVAL_MS);

    return clearMaintenancePoll;
  }, [bypassEnabled, checkMaintenanceMode, clearMaintenancePoll, maintenanceMode]);

  const showApp = bypassEnabled || !maintenanceMode;

  return (
    <>
      {/* Keep children mounted so providers (e.g. reCAPTCHA) are not torn down
          and re-injected when maintenance mode toggles. */}
      <div
        style={showApp ? undefined : { display: 'none' }}
        aria-hidden={!showApp}>
        {children}
      </div>
      <MaintenanceModal
        show={!bypassEnabled && maintenanceMode}
        onRetry={handleRetry}
        retrying={retrying}
      />
    </>
  );
};

export default MaintenanceGate;
